import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import axios from 'axios';
import Navbar from './Navbar';

const DEFAULT_THEME = {
  primaryColor: '#0F766E',
  secondaryColor: '#F8FAFC',
  accentColor: '#FF6B35',
  headingColor: '#0F172A',
  subheadingColor: '#475569',
  headingWeight: 700,
  subheadingWeight: 500,
  templateType: 'modern',
};

const TEMPLATE_LABELS = {
  modern: 'Modern',
  classic: 'Classic',
  bold: 'Bold',
};

const PREVIEW_SCREENS = ['menu', 'home', 'cart', 'profile'];

const PREVIEW_SCREEN_LABELS = {
  menu: 'Menu',
  home: 'Home',
  cart: 'Cart',
  profile: 'Profile',
};

const getPreviewTokens = (settings) => {
  const primary = settings.primaryColor;
  const secondary = settings.secondaryColor;
  const accent = settings.accentColor;

  if (settings.templateType === 'classic') {
    return {
      appBg: secondary,
      surface: '#FFFDF7',
      cardBorder: '#E7DBC4',
      titleColor: settings.headingColor || '#1F2937',
      subText: settings.subheadingColor || '#6B7280',
      headingWeight: Number(settings.headingWeight) || 700,
      subheadingWeight: Number(settings.subheadingWeight) || 500,
      primary,
      accent,
      searchBg: '#FFFFFF',
      searchBorder: '#E7DBC4',
      favoriteBg: '#FEF7F7',
      favoriteBorder: '#F5D0D0',
      itemImageBg: '#F3EDE0',
    };
  }

  if (settings.templateType === 'bold') {
    return {
      appBg: '#0B1020',
      surface: '#151C33',
      cardBorder: '#243252',
      titleColor: settings.headingColor || '#FFFFFF',
      subText: settings.subheadingColor || '#A5B4FC',
      headingWeight: Number(settings.headingWeight) || 700,
      subheadingWeight: Number(settings.subheadingWeight) || 500,
      primary,
      accent,
      searchBg: '#1A2544',
      searchBorder: '#2D3D64',
      favoriteBg: '#1F2B4D',
      favoriteBorder: '#34497A',
      itemImageBg: '#0F172A',
    };
  }

  return {
    appBg: secondary,
    surface: '#FFFFFF',
    cardBorder: '#E2E8F0',
    titleColor: settings.headingColor || '#0F172A',
    subText: settings.subheadingColor || '#475569',
    headingWeight: Number(settings.headingWeight) || 700,
    subheadingWeight: Number(settings.subheadingWeight) || 500,
    primary,
    accent,
    searchBg: '#FFFFFF',
    searchBorder: '#E2E8F0',
    favoriteBg: '#F8FAFC',
    favoriteBorder: '#E2E8F0',
    itemImageBg: '#E2E8F0',
  };
};

export default function CustomerAppThemeSettings() {
  const [settings, setSettings] = useState(DEFAULT_THEME);
  const [previewScreen, setPreviewScreen] = useState('menu');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const cmpID = user?.cmpID || 1;

  useEffect(() => {
    const loadSettings = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${global.SET_API_URL}customerTheme/public`, {
          params: { cmpID },
        });
        const data = response?.data?.data;
        if (data) {
          setSettings({
            primaryColor: data.primaryColor || DEFAULT_THEME.primaryColor,
            secondaryColor: data.secondaryColor || DEFAULT_THEME.secondaryColor,
            accentColor: data.accentColor || DEFAULT_THEME.accentColor,
            headingColor: data.headingColor || DEFAULT_THEME.headingColor,
            subheadingColor: data.subheadingColor || DEFAULT_THEME.subheadingColor,
            headingWeight: Number(data.headingWeight) || DEFAULT_THEME.headingWeight,
            subheadingWeight: Number(data.subheadingWeight) || DEFAULT_THEME.subheadingWeight,
            templateType: data.templateType || DEFAULT_THEME.templateType,
          });
        }
      } catch (error) {
        setMessage({ type: 'error', text: 'Failed to load customer app theme settings.' });
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, [cmpID]);

  const preview = useMemo(() => getPreviewTokens(settings), [settings]);

  const handleChange = (key) => (event) => {
    const value = event.target.value;
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const headingTextSx = {
    color: preview.titleColor,
    fontWeight: preview.headingWeight,
  };

  const subheadingTextSx = {
    color: preview.subText,
    fontWeight: preview.subheadingWeight,
  };

  const renderPreviewScreen = () => {
    if (previewScreen === 'home') {
      return (
        <Box sx={{ p: 1.5 }}>
          <Box sx={{ background: preview.primary, color: '#fff', p: 1.5, borderRadius: 2.2, mb: 1.1 }}>
            <Typography sx={{ fontWeight: preview.headingWeight, fontSize: 15 }}>Welcome to LuckyBraost</Typography>
            <Typography sx={{ fontSize: 11.5, mt: 0.5, opacity: 0.9, fontWeight: preview.subheadingWeight }}>Fast ordering with live menu and deals.</Typography>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 0.9, mb: 1.1 }}>
            {['Menu Items', 'Active Deals', 'Cart Qty'].map((label, idx) => (
              <Box key={label} sx={{ background: preview.surface, border: '1px solid', borderColor: preview.cardBorder, borderRadius: 1.6, p: 0.9 }}>
                <Typography sx={{ ...subheadingTextSx, fontSize: 10.5 }}>{label}</Typography>
                <Typography sx={{ ...headingTextSx, fontWeight: Math.max(preview.headingWeight, 800), fontSize: 18 }}>{idx + 3}</Typography>
              </Box>
            ))}
          </Box>

          {['Browse Menu', 'See Deals', 'Open Cart'].map((action) => (
            <Box key={action} sx={{ background: preview.surface, border: '1px solid', borderColor: preview.cardBorder, borderRadius: 1.8, p: 1.1, mb: 0.8 }}>
              <Typography sx={{ ...headingTextSx, fontSize: 13.5 }}>{action}</Typography>
              <Typography sx={{ ...subheadingTextSx, fontSize: 11.2 }}>Tap to continue</Typography>
            </Box>
          ))}
        </Box>
      );
    }

    if (previewScreen === 'cart') {
      return (
        <Box sx={{ p: 1.5 }}>
          <Box sx={{ background: preview.surface, border: '1px solid', borderColor: preview.cardBorder, borderRadius: 2, p: 1.2, mb: 1 }}>
            <Typography sx={{ ...headingTextSx, fontSize: 13 }}>Delivery Address</Typography>
            <Typography sx={{ ...subheadingTextSx, fontSize: 11.5, mt: 0.3 }}>Home, Gulberg - Lahore</Typography>
            <Typography sx={{ color: preview.primary, fontSize: 11, mt: 0.5, fontWeight: 700 }}>Zone: Serviceable</Typography>
          </Box>

          {['Zinger Burger', 'Chicken Chowmein'].map((item, idx) => (
            <Box key={item} sx={{ background: preview.surface, border: '1px solid', borderColor: preview.cardBorder, borderRadius: 2, p: 1.2, mb: 0.9 }}>
              <Typography sx={{ ...headingTextSx, fontSize: 13.5 }}>{item}</Typography>
              <Typography sx={{ color: preview.primary, fontSize: 12, mt: 0.35, fontWeight: 700 }}>Rs. {idx === 0 ? '490.00' : '620.00'}</Typography>
            </Box>
          ))}

          <Box sx={{ background: preview.surface, border: '1px solid', borderColor: preview.cardBorder, borderRadius: 2, p: 1.2, mb: 1 }}>
            <Typography sx={{ ...subheadingTextSx, fontSize: 11.2 }}>Grand Total</Typography>
            <Typography sx={{ ...headingTextSx, fontSize: 19, fontWeight: Math.max(preview.headingWeight, 800) }}>Rs. 1110.00</Typography>
          </Box>

          <Box sx={{ background: preview.primary, color: '#fff', textAlign: 'center', py: 1.1, borderRadius: 1.8, fontWeight: 700, fontSize: 13 }}>
            Place Order
          </Box>
        </Box>
      );
    }

    if (previewScreen === 'profile') {
      return (
        <Box sx={{ p: 1.5 }}>
          <Box sx={{ background: preview.surface, border: '1px solid', borderColor: preview.cardBorder, borderRadius: 2, p: 1.2, mb: 1 }}>
            <Box sx={{ display: 'flex', gap: 1.1, alignItems: 'center' }}>
              <Box sx={{ width: 46, height: 46, borderRadius: '50%', background: preview.primary }} />
              <Box>
                <Typography sx={{ ...headingTextSx, fontSize: 14 }}>Muhammad Ahmed</Typography>
                <Typography sx={{ ...subheadingTextSx, fontSize: 11.5 }}>Verified</Typography>
              </Box>
            </Box>
            <Box sx={{ mt: 1 }}>
              <Typography sx={{ ...subheadingTextSx, fontSize: 11.2 }}>Profile Completion</Typography>
              <Box sx={{ mt: 0.45, background: preview.cardBorder, borderRadius: 99, height: 8, overflow: 'hidden' }}>
                <Box sx={{ width: '75%', background: preview.primary, height: '100%' }} />
              </Box>
            </Box>
          </Box>

          {['My Addresses', 'My Orders', 'Wallet Balance'].map((row) => (
            <Box key={row} sx={{ background: preview.surface, border: '1px solid', borderColor: preview.cardBorder, borderRadius: 2, p: 1.1, mb: 0.7 }}>
              <Typography sx={{ ...headingTextSx, fontWeight: Math.max(preview.headingWeight, 600), fontSize: 13 }}>{row}</Typography>
            </Box>
          ))}

          <Box sx={{ border: '1px solid #FECACA', background: '#FEF2F2', color: '#DC2626', textAlign: 'center', borderRadius: 1.8, py: 1, mt: 0.6, fontWeight: 700, fontSize: 12.5 }}>
            Logout
          </Box>
        </Box>
      );
    }

    return (
      <Box sx={{ p: 1.5 }}>
        <Box
          sx={{
            borderRadius: 2.2,
            px: 1.3,
            py: 1,
            background: preview.searchBg,
            border: '1px solid',
            borderColor: preview.searchBorder,
            mb: 1,
          }}
        >
          <Typography sx={{ ...subheadingTextSx, fontSize: 12.5 }}>
            Search menu or category
          </Typography>
        </Box>

        <Typography sx={{ color: preview.primary, fontSize: 11.5, px: 0.2, mb: 0.9 }}>
          Refreshing menu...
        </Typography>

        {[{
          name: 'Zinger Burger',
          category: 'Burger',
          price: '490.00',
        }, {
          name: 'Chicken Chowmein',
          category: 'Chinese',
          price: '620.00',
        }].map((item) => (
          <Box
            key={item.name}
            sx={{
              borderRadius: 2,
              overflow: 'hidden',
              background: preview.surface,
              mb: 1.1,
              border: '1px solid',
              borderColor: preview.cardBorder,
            }}
          >
            <Box sx={{ display: 'flex', gap: 1.1, p: 1.3, alignItems: 'flex-start' }}>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ ...headingTextSx, fontSize: 14 }}>
                  {item.name}
                </Typography>
                <Typography sx={{ ...subheadingTextSx, fontSize: 12, mt: 0.25 }}>
                  {item.category}
                </Typography>
                <Typography sx={{ color: preview.primary, fontWeight: 700, fontSize: 13.5, mt: 0.5 }}>
                  Rs. {item.price}
                </Typography>
              </Box>

              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: 16,
                  border: '1px solid',
                  borderColor: preview.favoriteBorder,
                  background: preview.favoriteBg,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#64748B',
                  fontSize: 14,
                  lineHeight: 1,
                  flexShrink: 0,
                }}
              >
                ♡
              </Box>

              <Box
                sx={{
                  width: 62,
                  height: 62,
                  borderRadius: 1.5,
                  background: preview.itemImageBg,
                  flexShrink: 0,
                }}
              />
            </Box>

            <Box
              sx={{
                background: preview.primary,
                color: '#fff',
                textAlign: 'center',
                py: 1.05,
                fontSize: 12.5,
                fontWeight: 700,
              }}
            >
              Add to Cart
            </Box>
          </Box>
        ))}

        <Box
          sx={{
            mt: 0.6,
            borderRadius: 2,
            border: '1px dashed',
            borderColor: preview.cardBorder,
            textAlign: 'center',
            py: 1,
          }}
        >
          <Typography sx={{ ...subheadingTextSx, fontSize: 11.5 }}>
            No menu items found for this search.
          </Typography>
        </Box>
      </Box>
    );
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage({ type: '', text: '' });

      await axios.post(`${global.SET_API_URL}customerTheme/upsert`, {
        cmpID,
        ...settings,
        updatedBy: user?.userName || user?.dispName || 'web-admin',
      });

      setMessage({ type: 'success', text: 'Customer mobile app theme saved successfully.' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save settings. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Navbar />
      <Box sx={{ p: 2 }}>
        <Card sx={{ maxWidth: 1100, mx: 'auto' }}>
          <CardContent>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              Customer Mobile Theme Designer
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select colors, typography, and template style. Changes are synced to customer mobile app.
            </Typography>

            {message.text ? (
              <Alert severity={message.type === 'success' ? 'success' : 'error'} sx={{ mb: 2 }}>
                {message.text}
              </Alert>
            ) : null}

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <Box sx={{ flex: 1 }}>
                <Stack spacing={2}>
                  <TextField
                    label="Primary Color"
                    type="color"
                    value={settings.primaryColor}
                    onChange={handleChange('primaryColor')}
                    InputLabelProps={{ shrink: true }}
                    disabled={loading}
                  />
                  <TextField
                    label="Secondary Color"
                    type="color"
                    value={settings.secondaryColor}
                    onChange={handleChange('secondaryColor')}
                    InputLabelProps={{ shrink: true }}
                    disabled={loading}
                  />
                  <TextField
                    label="Accent Color"
                    type="color"
                    value={settings.accentColor}
                    onChange={handleChange('accentColor')}
                    InputLabelProps={{ shrink: true }}
                    disabled={loading}
                  />
                  <TextField
                    label="Heading Text Color"
                    type="color"
                    value={settings.headingColor}
                    onChange={handleChange('headingColor')}
                    InputLabelProps={{ shrink: true }}
                    disabled={loading}
                  />
                  <TextField
                    label="Subheading Text Color"
                    type="color"
                    value={settings.subheadingColor}
                    onChange={handleChange('subheadingColor')}
                    InputLabelProps={{ shrink: true }}
                    disabled={loading}
                  />

                  <FormControl fullWidth>
                    <InputLabel id="heading-weight-label">Heading Font Weight</InputLabel>
                    <Select
                      labelId="heading-weight-label"
                      value={settings.headingWeight}
                      label="Heading Font Weight"
                      onChange={handleChange('headingWeight')}
                      disabled={loading}
                    >
                      <MenuItem value={500}>500</MenuItem>
                      <MenuItem value={600}>600</MenuItem>
                      <MenuItem value={700}>700</MenuItem>
                      <MenuItem value={800}>800</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl fullWidth>
                    <InputLabel id="subheading-weight-label">Subheading Font Weight</InputLabel>
                    <Select
                      labelId="subheading-weight-label"
                      value={settings.subheadingWeight}
                      label="Subheading Font Weight"
                      onChange={handleChange('subheadingWeight')}
                      disabled={loading}
                    >
                      <MenuItem value={400}>400</MenuItem>
                      <MenuItem value={500}>500</MenuItem>
                      <MenuItem value={600}>600</MenuItem>
                      <MenuItem value={700}>700</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl fullWidth>
                    <InputLabel id="template-type-label">Template Type</InputLabel>
                    <Select
                      labelId="template-type-label"
                      value={settings.templateType}
                      label="Template Type"
                      onChange={handleChange('templateType')}
                      disabled={loading}
                    >
                      <MenuItem value="modern">Modern</MenuItem>
                      <MenuItem value="classic">Classic</MenuItem>
                      <MenuItem value="bold">Bold</MenuItem>
                    </Select>
                  </FormControl>

                  <Button variant="contained" onClick={handleSave} disabled={loading || saving}>
                    {saving ? 'Saving...' : 'Save Theme Settings'}
                  </Button>
                </Stack>
              </Box>

              <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />

              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                  Live Mobile Preview: {PREVIEW_SCREEN_LABELS[previewScreen]} ({TEMPLATE_LABELS[settings.templateType]})
                </Typography>

                <Stack direction="row" spacing={1} sx={{ mb: 1.2, flexWrap: 'wrap' }}>
                  {PREVIEW_SCREENS.map((screen) => (
                    <Button
                      key={screen}
                      variant={previewScreen === screen ? 'contained' : 'outlined'}
                      onClick={() => setPreviewScreen(screen)}
                      sx={{
                        minWidth: 68,
                        fontSize: 11.5,
                        px: 1.2,
                        py: 0.35,
                        background: previewScreen === screen ? preview.primary : 'transparent',
                        borderColor: preview.primary,
                        color: previewScreen === screen ? '#fff' : preview.primary,
                        '&:hover': {
                          background: previewScreen === screen ? preview.primary : `${preview.primary}12`,
                          borderColor: preview.primary,
                        },
                      }}
                    >
                      {PREVIEW_SCREEN_LABELS[screen]}
                    </Button>
                  ))}
                </Stack>

                <Box
                  sx={{
                    width: 305,
                    borderRadius: 4,
                    border: '8px solid #0f172a',
                    overflow: 'hidden',
                    background: preview.appBg,
                    mx: 'auto',
                    boxShadow: 4,
                  }}
                >
                  <Box sx={{ background: preview.primary, color: '#fff', px: 2, py: 1.2 }}>
                    <Typography variant="subtitle2" fontWeight={700}>
                      {PREVIEW_SCREEN_LABELS[previewScreen]}
                    </Typography>
                  </Box>

                  {renderPreviewScreen()}
                </Box>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    </>
  );
}
